import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Coffee, LogOut, Menu, X } from 'lucide-react';
import logo from '../assets/img/logo2.png';

const BUY_ME_A_COFFEE_URL = 'https://www.buymeacoffee.com/desipilla';

const Header = () => {
  const { isAuthenticated, user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = user?.email
    ? user.email[0].toUpperCase()
    : '?';

  return (
    <header className="bg-forest border-b-4 border-mustard sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 h-[70px] flex items-center justify-between">

        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <img
            src={logo}
            alt="Dorito Stats"
            className="h-12 brightness-0 invert"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {isAuthenticated && (
            <>
              <Link
                to="/dashboard"
                className="text-white/75 text-sm font-bold tracking-widest uppercase hover:text-mustard transition-colors"
              >
                Dashboard
              </Link>
              <span className="relative group">
                <span className="text-white/30 text-sm font-bold tracking-widest uppercase cursor-not-allowed select-none">
                  Playoff Pool
                </span>
                <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  Coming soon!
                </span>
              </span>
              <a
                href={BUY_ME_A_COFFEE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-mustard/45 bg-mustard/10 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-mustard transition-colors hover:bg-mustard hover:text-forest"
              >
                <Coffee className="h-3.5 w-3.5" />
                Buy Me a Coffee
              </a>
            </>
          )}
        </nav>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-4">
          {!loading && (
            isAuthenticated ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-[0.7rem] bg-white/[0.08] border border-white/20 rounded-full py-[0.35rem] pr-4 pl-[0.4rem]">
                  <div className="w-7 h-7 rounded-full bg-mustard text-forest font-extrabold text-xs flex items-center justify-center shrink-0">
                    {initials}
                  </div>
                  <span className="text-[0.82rem] text-white/85 font-semibold">
                    {user?.email}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 text-white/60 hover:text-mustard text-xs font-bold tracking-wider uppercase transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link
                  to="/login"
                  className="text-white/75 text-sm font-bold tracking-widest uppercase hover:text-mustard transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="bg-mustard text-forest text-sm font-extrabold px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors"
                >
                  Register
                </Link>
              </div>
            )
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-white/75 hover:text-mustard"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-forest border-t border-white/10 px-6 pb-4 flex flex-col gap-4">
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                className="text-white/75 text-sm font-bold tracking-widest uppercase hover:text-mustard pt-4"
              >
                Dashboard
              </Link>
              <span className="relative group inline-block">
                <span className="text-white/30 text-sm font-bold tracking-widest uppercase cursor-not-allowed select-none">
                  Playoff Pool
                </span>
                <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  Coming soon!
                </span>
              </span>
              <a
                href={BUY_ME_A_COFFEE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="inline-flex w-fit items-center gap-2 rounded-full border border-mustard/45 bg-mustard/10 px-3 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-mustard transition-colors hover:bg-mustard hover:text-forest"
              >
                <Coffee className="h-4 w-4" />
                Buy Me a Coffee
              </a>

              <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                <span className="text-white/60 text-sm">{user?.email}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 text-white/60 hover:text-mustard text-xs font-bold tracking-wider uppercase"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <div className="flex gap-4 pt-4">
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="text-white/75 text-sm font-bold tracking-widest uppercase hover:text-mustard"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                onClick={() => setMobileOpen(false)}
                className="text-white/75 text-sm font-bold tracking-widest uppercase hover:text-mustard"
              >
                Register
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
